import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  CheckCircle,
  Zap,
  Database,
  Cloud,
  TrendingUp,
  Shield,
  Sparkles,
  Rocket,
} from "lucide-react";

import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import GetStartedDialog from "@/components/GetStartedDialog";

import s4hanaImage from "@/assets/sap-s4hana-dashboard.jpg";

const SAPS4HANARiseDetail = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const coreCapabilities = [
    {
      icon: Database,
      title: "SAP HANA In-Memory Platform",
      description:
        "Real-time analytics and lightning-fast transactional processing powered by SAP HANA.",
    },
    {
      icon: Cloud,
      title: "Cloud, Hybrid & On-Premise",
      description:
        "Flexible deployment options with seamless scalability and modernization.",
    },
    {
      icon: Sparkles,
      title: "Embedded Intelligence",
      description:
        "AI, machine learning, automation, and predictive analytics built directly into business processes.",
    },
    {
      icon: TrendingUp,
      title: "Simplified Digital Core",
      description:
        "Streamlined architecture with reduced data redundancy and accelerated operations.",
    },
  ];

  const transformationAreas = [
    {
      title: "Finance Transformation",
      items: [
        "Universal Journal architecture",
        "Real-time financial closing",
        "Predictive cash flow analytics",
        "Embedded compliance & governance",
      ],
    },
    {
      title: "Supply Chain & Manufacturing",
      items: [
        "Integrated production planning",
        "Warehouse & inventory optimization",
        "Predictive maintenance",
        "Industry 4.0 enablement",
      ],
    },
    {
      title: "Sales & Customer Experience",
      items: [
        "Omnichannel order management",
        "Real-time pricing & fulfillment",
        "Integrated CRM capabilities",
        "Advanced customer insights",
      ],
    },
    {
      title: "Enterprise Innovation",
      items: [
        "SAP Business Technology Platform",
        "Low-code/no-code extensibility",
        "AI & IoT integrations",
        "Continuous innovation updates",
      ],
    },
  ];

  const riseBenefits = [
    {
      icon: Rocket,
      title: "Accelerated Transformation",
      description:
        "Rapid deployment using SAP Activate methodology and industry best practices.",
    },
    {
      icon: Shield,
      title: "Managed Cloud Operations",
      description:
        "SAP-managed infrastructure, security, compliance, and lifecycle management.",
    },
    {
      icon: Cloud,
      title: "Subscription-Based Model",
      description:
        "Reduce upfront infrastructure investments with predictable operating costs.",
    },
    {
      icon: Zap,
      title: "Continuous Innovation",
      description:
        "Automatic quarterly innovation updates with access to emerging SAP technologies.",
    },
  ];

  const businessBenefits = [
    "Up to 50–70% reduction in database footprint with simplified data models",
    "Real-time reporting and analytics without separate BW environments",
    "Faster implementations using fit-to-standard SAP best practices",
    "Lower total cost of ownership with SAP-managed cloud infrastructure",
    "Continuous access to AI, ML, automation, and intelligent business processes",
    "Modern SAP Fiori user experience across all enterprise functions",
    "Scalable architecture supporting future digital transformation initiatives",
    "Integrated clean-core strategy for sustainable innovation",
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <div>
        {/* HERO */}
        <div className="py-20 bg-gradient-to-br from-primary/10 via-secondary/10 to-background">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* <Button
              variant="ghost"
              onClick={() => window.history.back()}
              className="mb-6"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button> */}

              <div className="space-y-5">
                {/* <Badge className="bg-primary text-white hover:bg-primary/90">
                Intelligent ERP & Business Transformation
              </Badge> */}

                <h1 className="text-4xl md:text-5xl font-bold text-foreground leading-tight">
                  SAP S/4HANA & RISE with SAP
                  <span className="block bg-gradient-primary bg-clip-text text-transparent mt-2">
                    Digital Core Transformation
                  </span>
                </h1>

                <p className="text-xl text-muted-foreground max-w-4xl leading-relaxed">
                  Modernize your enterprise with SAP S/4HANA and RISE with SAP —
                  combining intelligent ERP, cloud transformation, embedded AI,
                  business process optimization, and managed innovation into one
                  unified digital transformation journey.
                </p>
              </div>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={s4hanaImage}
                    alt="SAP S/4HANA and RISE with SAP transformation platform"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* HERO IMAGE */}

        {/* CORE CAPABILITIES */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              Core Transformation Capabilities
            </h2>

            <p className="text-muted-foreground max-w-3xl mx-auto">
              Intelligent ERP powered by SAP HANA with integrated cloud,
              automation, analytics, and enterprise modernization services.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreCapabilities.map((feature, idx) => (
              <Card
                key={idx}
                className="hover:shadow-lg transition-shadow bg-gradient-card border-0"
              >
                <CardHeader>
                  <feature.icon
                    className={`h-12 w-12 ${idx % 2 === 0 ? "text-primary shadow-glow" : "text-secondary shadow-glow-secondary"} mb-4`}
                  />

                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </CardHeader>

                <CardContent>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* TRANSFORMATION AREAS */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              Enterprise Transformation Areas
            </h2>

            <p className="text-muted-foreground max-w-3xl mx-auto">
              End-to-end modernization across finance, supply chain,
              manufacturing, customer experience, and enterprise innovation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {transformationAreas.map((area, idx) => (
              <Card key={idx} className="bg-gradient-card border-0">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center">
                    <CheckCircle
                      className={`h-6 w-6 ${idx % 2 === 0 ? "text-primary shadow-glow" : "text-secondary shadow-glow-secondary"} mr-3`}
                    />
                    {area.title}
                  </CardTitle>
                </CardHeader>

                <CardContent>
                  <ul className="space-y-3">
                    {area.items.map((item, iIdx) => (
                      <li key={iIdx} className="flex items-start">
                        <span
                          className={`inline-block w-2 h-2 rounded-full ${iIdx % 2 === 0 ? "bg-primary shadow-glow" : "bg-secondary shadow-glow-secondary"} mt-2 mr-3 flex-shrink-0`}
                        />

                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* RISE BENEFITS */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Why RISE with SAP?</h2>

            <p className="text-muted-foreground max-w-3xl mx-auto">
              A bundled transformation-as-a-service approach accelerating your
              move to the intelligent enterprise.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {riseBenefits.map((benefit, idx) => (
              <Card
                key={idx}
                className="hover:shadow-lg transition-shadow bg-gradient-card border-0"
              >
                <CardHeader>
                  <benefit.icon
                    className={`h-12 w-12 ${idx % 2 === 0 ? "text-primary shadow-glow" : "text-secondary shadow-glow-secondary"} mb-4`}
                  />

                  <CardTitle className="text-lg">{benefit.title}</CardTitle>
                </CardHeader>

                <CardContent>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* BUSINESS BENEFITS */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="bg-gradient-to-br from-primary/10 via-background to-secondary/10">
            <CardHeader>
              <CardTitle className="text-2xl">
                Business Outcomes & Value
              </CardTitle>

              <p className="text-muted-foreground mt-2">
                Measurable impact across your digital transformation journey
              </p>
            </CardHeader>

            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {businessBenefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start">
                    <CheckCircle
                      className={`h-5 w-5 ${idx % 2 === 0 ? "text-primary shadow-glow" : "text-secondary shadow-glow-secondary"} mr-3 mt-0.5 flex-shrink-0`}
                    />

                    <span className="text-muted-foreground">{benefit}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="bg-gradient-to-r from-primary to-secondary text-white border-0 shadow-glow">
            <CardContent className="p-8 text-center">
              <h2 className="text-3xl font-bold mb-4">
                Ready to Transform Your Enterprise?
              </h2>

              <p className="text-xl mb-6 text-white/90">
                Accelerate your digital transformation with SAP S/4HANA and RISE
                with SAP solutions tailored to your business.
              </p>

              <GetStartedDialog />
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SAPS4HANARiseDetail;
